import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';

/**
 * Changelog Page - Version history and release notes
 * Modern UI with timeline, collapsible sections, and navigation
 */

// Version data structure
interface ChangeItem {
  text: string;
  type: 'feature' | 'fix' | 'improvement' | 'security' | 'breaking';
}

interface VersionSection {
  icon: string;
  title: string;
  items: ChangeItem[];
  color: string;
}

interface Version {
  version: string;
  title: string;
  date: string;
  relativeDate?: string;
  isCurrent?: boolean;
  type: 'major' | 'minor' | 'patch';
  sections: VersionSection[];
}

// Safe text renderer that converts `code` to <code> elements without dangerouslySetInnerHTML
function SafeCodeText({ text }: { text: string }) {
  // Split text by backtick-wrapped content
  const parts = text.split(/`([^`]+)`/g);

  return (
    <>
      {parts.map((part, idx) => {
        // Odd indices are the captured groups (code content)
        if (idx % 2 === 1) {
          return (
            <code
              key={idx}
              style={{
                background: 'var(--hover)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              {part}
            </code>
          );
        }
        return <span key={idx}>{part}</span>;
      })}
    </>
  );
}

// Badge component for change types
function TypeBadge({ type }: { type: ChangeItem['type'] }) {
  const config = {
    feature: { bg: '#10b981', text: 'NEW' },
    fix: { bg: '#ef4444', text: 'FIX' },
    improvement: { bg: '#3b82f6', text: 'UPD' },
    security: { bg: '#f59e0b', text: 'SEC' },
    breaking: { bg: '#dc2626', text: '⚠️' },
  };

  const { bg, text } = config[type];

  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      borderRadius: '4px',
      fontSize: '9px',
      fontWeight: 700,
      background: bg,
      color: 'white',
      marginRight: '8px',
      verticalAlign: 'middle',
    }}>
      {text}
    </span>
  );
}

// Version type badge
function VersionTypeBadge({ type }: { type: Version['type'] }) {
  const config = {
    major: { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: 'MAJOR' },
    minor: { bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', text: 'MINOR' },
    patch: { bg: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', text: 'PATCH' },
  };

  const { bg, text } = config[type];

  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '10px',
      fontWeight: 700,
      background: bg,
      color: 'white',
      marginLeft: '8px',
    }}>
      {text}
    </span>
  );
}

// Timeline dot component
function TimelineDot({ isCurrent, color }: { isCurrent?: boolean; color: string }) {
  return (
    <div style={{
      position: 'absolute',
      left: '-28px',
      top: '24px',
      width: isCurrent ? '16px' : '12px',
      height: isCurrent ? '16px' : '12px',
      borderRadius: '50%',
      background: isCurrent ? color : 'var(--card)',
      border: `3px solid ${color}`,
      boxShadow: isCurrent ? `0 0 12px ${color}40` : 'none',
      transition: 'all 0.3s ease',
    }} />
  );
}

// Collapsible version card
function VersionCard({
  version,
  isExpanded,
  onToggle,
  index
}: {
  version: Version;
  isExpanded: boolean;
  onToggle: () => void;
  index: number;
}) {
  const accentColor = version.isCurrent ? 'var(--accent)' : 'var(--muted)';

  return (
    <div
      className="card"
      style={{
        position: 'relative',
        marginLeft: '20px',
        borderLeft: `3px solid ${version.isCurrent ? 'var(--accent)' : 'var(--border)'}`,
        transition: 'all 0.3s ease',
        animation: `fadeSlideIn 0.4s ease ${index * 0.05}s both`,
      }}
    >
      <TimelineDot isCurrent={version.isCurrent} color={version.isCurrent ? 'var(--accent)' : 'var(--muted)'} />

      {/* Header - Always visible */}
      <div
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 700,
            color: version.isCurrent ? 'var(--accent)' : 'var(--text)',
            margin: 0,
          }}>
            v{version.version}
          </h2>

          <span style={{
            fontSize: '14px',
            color: 'var(--text)',
            fontWeight: 500,
          }}>
            {version.title}
          </span>

          {version.isCurrent && (
            <span style={{
              padding: '4px 12px',
              background: 'var(--accent)',
              color: 'white',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 600,
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              CURRENT
            </span>
          )}

          <VersionTypeBadge type={version.type} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            fontSize: '12px',
            color: 'var(--muted)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Calendar size={14} />
            {version.date}
            {version.relativeDate && (
              <span style={{ opacity: 0.7 }}> • {version.relativeDate}</span>
            )}
          </span>

          <div style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            color: 'var(--muted)',
          }}>
            <Icons.ChevronDown size={20} />
          </div>
        </div>
      </div>

      {/* Content - Collapsible */}
      <div style={{
        maxHeight: isExpanded ? '2000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.4s ease-in-out',
        opacity: isExpanded ? 1 : 0,
      }}>
        <div style={{ paddingTop: '16px', borderTop: '1px solid var(--border)', marginTop: '16px' }}>
          {version.sections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: idx < version.sections.length - 1 ? '20px' : 0 }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: 600,
                marginBottom: '12px',
                color: section.color,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span>{section.icon}</span>
                {section.title}
              </h3>
              <ul style={{
                fontSize: '13px',
                lineHeight: '1.9',
                paddingLeft: '20px',
                margin: 0,
              }}>
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} style={{ marginBottom: '4px' }}>
                    <TypeBadge type={item.type} />
                    <SafeCodeText text={item.text} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Quick navigation component
function QuickNav({ versions, onJump }: { versions: Version[]; onJump: (version: string) => void }) {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      padding: '12px 16px',
      background: 'var(--hover)',
      borderRadius: '12px',
      marginBottom: '8px',
    }}>
      <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600, marginRight: '8px' }}>
        Jump to:
      </span>
      {versions.slice(0, 8).map((v) => (
        <button
          key={v.version}
          onClick={() => onJump(v.version)}
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            border: 'none',
            background: v.isCurrent ? 'var(--accent)' : 'var(--card)',
            color: v.isCurrent ? 'white' : 'var(--text)',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          v{v.version}
        </button>
      ))}
    </div>
  );
}

// Stats component
function ChangelogStats({ versions }: { versions: Version[] }) {
  const stats = useMemo(() => {
    let features = 0, fixes = 0, improvements = 0;
    versions.forEach(v => {
      v.sections.forEach(s => {
        s.items.forEach(i => {
          if (i.type === 'feature') features++;
          else if (i.type === 'fix') fixes++;
          else improvements++;
        });
      });
    });
    return { features, fixes, improvements, total: features + fixes + improvements };
  }, [versions]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '12px',
      marginBottom: '8px',
    }}>
      {[
        { label: 'Versions', value: versions.length, color: '#8b5cf6', icon: '📦' },
        { label: 'Features', value: stats.features, color: '#10b981', icon: '✨' },
        { label: 'Fixes', value: stats.fixes, color: '#ef4444', icon: '🔧' },
        { label: 'Total Changes', value: stats.total, color: '#3b82f6', icon: '📝' },
      ].map((stat) => (
        <div key={stat.label} style={{
          padding: '16px',
          background: 'var(--card)',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: '24px', marginBottom: '4px' }}>{stat.icon}</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600 }}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

export function Changelog() {
  const { t } = useTranslation();
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set(['7.0']));
  const [searchQuery, setSearchQuery] = useState('');

  // Toggle version expansion
  const toggleVersion = (version: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  // Expand all / Collapse all
  const expandAll = () => {
    setExpandedVersions(new Set(versions.map(v => v.version)));
  };

  const collapseAll = () => {
    setExpandedVersions(new Set(['7.0']));
  };

  // Jump to version
  const jumpToVersion = (version: string) => {
    setExpandedVersions(prev => new Set([...prev, version]));
    setTimeout(() => {
      const element = document.getElementById(`version-${version}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Version data - using translations where available
  const versions: Version[] = useMemo(() => [
    {
      version: '7.1',
      title: 'Portfolio reliability + Glass Hero + Honest Analytics + UX Polish',
      date: '6 May 2026',
      relativeDate: t('changelog.today', 'Today'),
      isCurrent: true,
      type: 'major',
      sections: [
        {
          icon: '🛟',
          title: 'Portfolio Reliability — V1 skeleton fix + CRUD optimistic',
          color: '#10b981',
          items: [
            { text: 'V1 skeleton infini bug FIXED — stale-while-revalidate cache pattern : reads cache first, never overwrites with empty result, never recreates empty default if cache has portfolios', type: 'fix' as const },
            { text: 'Position update no longer hangs the modal on "Saving…" — switched to OPTIMISTIC pattern : local state + cache updated immediately, Supabase syncs in background (no more visible 5s timeout)', type: 'fix' as const },
            { text: 'Position update no longer wipes `coin_id` (data-loss bug) — merge instead of replace preserves client-enriched fields', type: 'fix' as const },
            { text: 'Position add — client-generated UUID + optimistic insert (same pattern as update)', type: 'fix' as const },
            { text: 'Position delete — optimistic delete with background sync', type: 'fix' as const },
            { text: 'Realtime UPDATE handler also merge-instead-of-replace (was wiping coin_id from postgres_changes payload)', type: 'fix' as const },
            { text: 'Added `if (!data) throw` guards so silent Supabase nulls don\'t poison local state', type: 'security' as const },
          ],
        },
        {
          icon: '✨',
          title: 'Dashboard "Glass Hero" V3 (now default)',
          color: '#8b5cf6',
          items: [
            { text: 'New `DashboardHeaderV3` — single Hero glass card with mood-ring (data-mood attr colours the radial gradient by P&L sign)', type: 'feature' as const },
            { text: 'Big primary value (44 px) + delta pill + sub-info (BTC ratio, invested) — north-star always visible', type: 'feature' as const },
            { text: 'Hero chart full-width + range selector (24h/7j/30j/3M/1A/Tout) + vs BTC switch — fully wired', type: 'feature' as const },
            { text: 'Mini portfolio donut absolute-positioned top-right inside the chart, click → analytics/Overview', type: 'feature' as const },
            { text: 'Hide values icon-only Eye toggle — Total Value stays visible (north-star), only delta + sub-info + KPI cards blurred', type: 'feature' as const },
            { text: '4-card KPI sub-row (Realized / Unrealized / Diversification / Invested) + 5th additive `vs BTC` card when comparison is on', type: 'feature' as const },
            { text: 'Smart Insights collapsible at the bottom (state persisted)', type: 'feature' as const },
            { text: 'V3 is now the DEFAULT — opt-out via `localStorage.setItem(\'disable_dashboard_header_v3\', \'true\')`', type: 'breaking' as const },
          ],
        },
        {
          icon: '📊',
          title: 'Honest Analytics — death of synthetic data',
          color: '#3b82f6',
          items: [
            { text: '`PortfolioValueChart` rebuilt deterministically from positions × current prices (was Math.sin + Math.random fake waves)', type: 'fix' as const },
            { text: '`MetricCardWithSparkline` — sparklines hidden when no real data (was generating Math.sin random fake curves)', type: 'fix' as const },
            { text: '`DrawdownChart` rebuilt deterministically (was Math.random daily variation)', type: 'fix' as const },
            { text: '`MultiTimelineView` removed entirely — was 100 % fake. Real multi-period comparison lands later with snapshots', type: 'breaking' as const },
            { text: 'New `RealizedUnrealizedChart` — cumulative P&L decomposition over time (FIFO walk). Useful for tax-loss harvesting', type: 'feature' as const },
            { text: 'Insights page : real Sharpe ratio from `usePortfolioSnapshots` daily NAV (annualized × √252) — falls back to "—" with pedagogical tooltip when < 7 snapshots', type: 'feature' as const },
            { text: 'Insights page : heuristic correlation column replaced with real "Contribution P&L %" (each asset\'s share of total absolute P&L)', type: 'fix' as const },
            { text: 'Insights timeframe selector (7d/30d/90d/1y) finally wired to the metrics — filters positions by date', type: 'fix' as const },
            { text: '`tokenVsBTC` and `positionVsBTC` now return `null` (rendered as "—") when BTC historical price missing — no more misleading +0.00 %', type: 'fix' as const },
          ],
        },
        {
          icon: '🏆',
          title: 'Best-in-class Analytics additions',
          color: '#f59e0b',
          items: [
            { text: 'New `TaxLotViewer` (Position Management page) — interactive FIFO lot table with court/long term badge, days-until-long-term countdown, sortable columns, "short term only" filter', type: 'feature' as const },
            { text: 'New `SectorExposurePie` (Portfolio Overview page) — donut grouping tokens by sector (Layer 1 / Layer 2 / DeFi / Stables / Memes / Storage / Oracles / Gaming / Privacy), Herfindahl concentration index + auto warning when a sector > 50 %', type: 'feature' as const },
            { text: 'New `DrawdownDurationWaterfall` (Performance Analytics page) — detects drawdown episodes from the deterministic value-series, bars sorted worst-first showing recovery duration', type: 'feature' as const },
          ],
        },
        {
          icon: '🧹',
          title: 'Analytics cleanup + tabbed AnalyticsHub',
          color: '#06b6d4',
          items: [
            { text: 'AnalyticsHub — 5 analytics pages (Overview / Performance / Risk / Trading / Positions) + new Insights tab consolidated behind a single tabbed shell with lazy loading', type: 'feature' as const },
            { text: 'New Insights page — fuses retired AdvancedAnalytics metrics with V3 widgets (HealthScore)', type: 'feature' as const },
            { text: 'Deduped `PortfolioHealthScore` (was in Overview + Insights — kept in Insights only)', type: 'fix' as const },
            { text: 'Deduped `SmartSuggestions` (was in Insights + PositionManagement — kept in PositionManagement only)', type: 'fix' as const },
          ],
        },
        {
          icon: '♿',
          title: 'UX & a11y polish',
          color: '#ec4899',
          items: [
            { text: 'Native `confirm()` replaced by `useConfirmation` + `ConfirmationModal` in 6 sites (AlertsPage delete/clear, PositionsTable delete, TargetPriceTracker × 3)', type: 'improvement' as const },
            { text: 'Settings API key create/revoke now show toast feedback (was silent)', type: 'improvement' as const },
            { text: 'MonitorTabs full ARIA tabs pattern : ←/→/Home/End keyboard nav, tabindex managed, aria-controls panel linking', type: 'improvement' as const },
            { text: 'Centralised brand colours in `src/config/monitorColors.ts` — `TRACKER_COLORS` was duplicated × 5, now single source of truth', type: 'improvement' as const },
            { text: 'AlertForm modal — Escape key closes (standard UX expectation that was missing)', type: 'improvement' as const },
            { text: 'New `<DataState>` smart wrapper + new `error` variant on `DataStateCard` — pattern wrapper that collapses loading/error/empty/data into one declarative component', type: 'feature' as const },
            { text: 'AlertsPage migrated to `<DataState>` — sync errors now surfaced with retry button (was silently ignored)', type: 'improvement' as const },
            { text: 'TaxCenter `TransactionList` migrated to `<ResponsiveTable>` — desktop table → mobile card stack', type: 'improvement' as const },
            { text: 'TaxStrategies — 2 reference tables (Lombard platforms + Crypto cards) migrated to `<ResponsiveTable>`', type: 'improvement' as const },
          ],
        },
      ],
    },
    {
      version: '7.0',
      title: 'Provider Platform, Homepage Redesign & Academy',
      date: '11 Feb 2026',
      relativeDate: undefined,
      isCurrent: false,
      type: 'major',
      sections: [
        {
          icon: '🏗️',
          title: 'Provider Platform — Data & UX',
          color: '#8b5cf6',
          items: [
            { text: 'Complete data storage — node history, revenue snapshots, registration types', type: 'feature' as const },
            { text: 'Sync filters, sort, pagination & panel state to URL params', type: 'feature' as const },
            { text: 'Add Changelog page to Provider Platform + cross-links between apps', type: 'feature' as const },
            { text: 'Separate managed providers from self-setup/dedicated on providers page', type: 'feature' as const },
            { text: 'Add spoken languages to provider cards and profiles', type: 'feature' as const },
            { text: 'Replace language text labels with flag emojis, add FR/DE to Test Provider', type: 'improvement' as const },
            { text: 'Use US flag for English language icon', type: 'improvement' as const },
            { text: 'Mark NodeOrbit as FluxLabs Partner and reorder after GoldieTech', type: 'improvement' as const },
            { text: 'Correct app expiry calculation — `expire` is relative duration, not absolute block height', type: 'fix' as const },
            { text: 'Resolve apps sync failures — add timeout, reduce payload sizes, fix FK violations', type: 'fix' as const },
            { text: 'Prioritize ArcaneOS in FluxOS setup guide, deprecate legacy', type: 'improvement' as const },
          ]
        },
        {
          icon: '🏠',
          title: 'Homepage Redesign',
          color: '#3b82f6',
          items: [
            { text: 'Clean up homepage — remove old sections, reorder into 7 clear sections', type: 'feature' as const },
            { text: 'Add dedicated platform landing page (WIP)', type: 'feature' as const },
            { text: 'Swap node tiers above ecosystem, green USD rewards, new title', type: 'feature' as const },
            { text: 'Add quick links grid and provider CTA to homepage', type: 'feature' as const },
            { text: 'Restore unified "Built on the Flux Ecosystem" section and display FAQ in two columns', type: 'improvement' as const },
            { text: 'Update homepage CTA to focus on earning $FLUX and getting first node online', type: 'improvement' as const },
            { text: 'Apply `gradient-text-flux` to $FLUX and Passive Income in tiers title', type: 'improvement' as const },
            { text: 'Add 10 FAQ items to homepage with accordion section', type: 'feature' as const },
            { text: 'Clarify NVIDIA NPN partnership is with InFlux Technologies, not RunOnFlux', type: 'fix' as const },
          ]
        },
        {
          icon: '🎓',
          title: 'Academy',
          color: '#10b981',
          items: [
            { text: 'Add 4 new Academy tracks (Titan Nodes, FluxAI/DePIN, Hosting Provider, Governance)', type: 'feature' as const },
            { text: 'Add 6 new Academy tracks (WIP) for Intermediate and Advanced levels', type: 'feature' as const },
            { text: 'Group Academy tracks by difficulty (Beginner / Intermediate / Advanced)', type: 'feature' as const },
            { text: 'Sort academy tracks by difficulty: beginner → intermediate → advanced', type: 'improvement' as const },
          ]
        },
        {
          icon: '📊',
          title: 'FluxView Core',
          color: '#f59e0b',
          items: [
            { text: 'Add blockchain revenue tracking (FluxTracker-style) to FluxView Core', type: 'feature' as const },
            { text: 'Merge unknown ArcaneOS nodes into "non" category', type: 'improvement' as const },
            { text: 'Fix RAM unit conversion — values now display in TB instead of GB', type: 'fix' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Bug Fixes & Technical',
          color: '#ef4444',
          items: [
            { text: 'Fix apps sync failing due to payload size and slow component upserts', type: 'fix' as const },
            { text: 'Fix heading level type error: change level 4 to 3', type: 'fix' as const },
            { text: 'Make mobile logo clickable to redirect to homepage', type: 'fix' as const },
            { text: 'Center WIP ribbon text on the corner diagonal', type: 'fix' as const },
            { text: 'Add Vercel Analytics to track visitors and page views', type: 'feature' as const },
          ]
        },
      ]
    },
    {
      version: '6.9',
      title: 'Showcase Site SEO, Blog, FAQ & Profitability Tools',
      date: '10 Feb 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '📝',
          title: 'Blog System (Showcase Site)',
          color: '#8b5cf6',
          items: [
            { text: 'Complete blog section with 9 articles translated to English', type: 'feature' as const },
            { text: 'French blog content + reading progress bar', type: 'feature' as const },
            { text: 'Add 2 missing blog articles (Guide DePIN + Cartographie Complete)', type: 'feature' as const },
            { text: 'Rename blog images to English slugs, convert PNG→WebP, add image display', type: 'improvement' as const },
            { text: 'Fix render HTML tags (`em`, `strong`) in blog article content', type: 'fix' as const },
          ]
        },
        {
          icon: '🔍',
          title: 'SEO & Performance',
          color: '#10b981',
          items: [
            { text: 'Add SSG, Rich Snippets (JSON-LD), and comprehensive SEO improvements', type: 'feature' as const },
            { text: 'New Fluxme screenshot as default OG image for homepage', type: 'improvement' as const },
            { text: 'Resolve 500 errors on SSG pages and add FAQ/Blog to navigation', type: 'fix' as const },
          ]
        },
        {
          icon: '❓',
          title: 'FAQ',
          color: '#3b82f6',
          items: [
            { text: 'Comprehensive FAQ page with 39 questions across 8 categories', type: 'feature' as const },
          ]
        },
        {
          icon: '💰',
          title: 'FluxNode Profitability',
          color: '#f59e0b',
          items: [
            { text: 'Add FluxNode profitability calculator', type: 'feature' as const },
            { text: 'Enhance Node Tiers section with live profitability data', type: 'feature' as const },
            { text: 'Show collateral USD value next to FLUX amount in tier cards', type: 'feature' as const },
            { text: 'Add profitability calculator link to AppSidebar', type: 'improvement' as const },
            { text: 'Prioritize FluxLabs Partners at the top of providers list', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Bug Fixes & Technical',
          color: '#ef4444',
          items: [
            { text: 'Add IP type detection, per-tier stats, fix enterprise index, disable CSV buttons', type: 'feature' as const },
            { text: 'Fix mobile sidebar showing only icons without text labels', type: 'fix' as const },
            { text: 'Add `sharp` as dev dependency for image conversion', type: 'improvement' as const },
            { text: 'Add `tsbuildinfo` to `.gitignore`', type: 'improvement' as const },
          ]
        },
      ]
    },
    {
      version: '6.8',
      title: 'FluxNode Payback Tracking, Benchmarks & Onboarding UX',
      date: '8 Feb 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🖥️',
          title: 'FluxNode Monitor - Payback & Benchmarks',
          color: '#3b82f6',
          items: [
            { text: 'Payback progress tracking: monitor ROI recovery for each node', type: 'feature' as const },
            { text: 'Custom tracking start date for accurate payback calculation', type: 'feature' as const },
            { text: 'Latest benchmarks display from `/benchmark/getbenchmarks` endpoint', type: 'feature' as const },
            { text: 'Show EPS score, ping, download/upload speeds, disk write performance', type: 'feature' as const },
            { text: '"PAID OFF" badge when node investment is fully recovered', type: 'feature' as const },
            { text: 'Monthly rewards estimation based on current FLUX price', type: 'feature' as const },
            { text: 'Payback and Benchmarks displayed side by side in list view', type: 'improvement' as const },
          ]
        },
        {
          icon: '📱',
          title: 'Onboarding Experience',
          color: '#10b981',
          items: [
            { text: 'Mobile swipe gestures for navigating between slides', type: 'feature' as const },
            { text: 'Simplified mobile navigation with arrows and dots', type: 'improvement' as const },
            { text: 'Full screen modal on mobile for better readability', type: 'improvement' as const },
            { text: 'Sticky footer navigation - always visible at bottom', type: 'fix' as const },
            { text: 'Fixed dots visibility and sizing on mobile', type: 'fix' as const },
            { text: 'Fixed navigation buttons cut off on small screens', type: 'fix' as const },
          ]
        },
        {
          icon: '🌐',
          title: 'Internationalization & Language',
          color: '#8b5cf6',
          items: [
            { text: 'Complete English translations for FluxNode components', type: 'feature' as const },
            { text: 'Complete English translations for Public API page', type: 'feature' as const },
            { text: 'FluxNode calculator translated to English', type: 'feature' as const },
            { text: 'Language switcher moved to Settings page for mobile portrait', type: 'improvement' as const },
            { text: 'Changed English flag from UK to US', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Bug Fixes & Technical',
          color: '#ef4444',
          items: [
            { text: 'Fixed Toast API initialization and missing icons (Lightbulb, Info)', type: 'fix' as const },
            { text: 'Disabled social login buttons (Google, X, Discord) pending configuration', type: 'fix' as const },
            { text: 'Removed subscription/pricing section from Public API page', type: 'improvement' as const },
            { text: 'Fixed AuthModal missing Icons import', type: 'fix' as const },
          ]
        },
      ]
    },
    {
      version: '6.7',
      title: 'FluxNode Maintenance Window, Datavex Integration & Social Login',
      date: '7 Feb 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🖥️',
          title: 'FluxNode Monitor Enhancements',
          color: '#3b82f6',
          items: [
            { text: 'Maintenance window display with countdown timer (based on 2ndtlmining/Fluxnode)', type: 'feature' as const },
            { text: 'Green badge when window is open, red badge when closed', type: 'feature' as const },
            { text: 'Datavex hosted badge showing subscription status on nodes', type: 'feature' as const },
            { text: 'Datavex subscription expiration date displayed on badges', type: 'feature' as const },
            { text: 'EPS (Earnings Per Share) score and Disk Write speed in expanded details', type: 'feature' as const },
            { text: 'Expandable node details in list view with accordion behavior', type: 'feature' as const },
            { text: 'PNR (Progressive Node Rewards) eligibility badge in list view', type: 'feature' as const },
            { text: 'Search filter by IP address or node alias', type: 'feature' as const },
            { text: 'Node aliases with grid/list view toggle', type: 'feature' as const },
            { text: 'Smart rank change alerts - ignores normal queue progression cycles', type: 'fix' as const },
            { text: 'Node aliases now sync to database instead of localStorage only', type: 'fix' as const },
            { text: 'Compact single-line rows in list view for better density', type: 'improvement' as const },
          ]
        },
        {
          icon: '🏠',
          title: 'Hosting Rig Redesign',
          color: '#10b981',
          items: [
            { text: 'Complete redesign with unified design system', type: 'feature' as const },
            { text: 'Datavex API integration for hosted nodes display', type: 'feature' as const },
            { text: 'Group hosted nodes by wallet in My Hostings section', type: 'feature' as const },
            { text: 'Filter out Datavex-hosted nodes from unhosted list', type: 'feature' as const },
            { text: 'Scrollable node selection list for better UX', type: 'fix' as const },
          ]
        },
        {
          icon: '🔐',
          title: 'Social Authentication',
          color: '#8b5cf6',
          items: [
            { text: 'Sign in with Google OAuth', type: 'feature' as const },
            { text: 'Sign in with X (Twitter) OAuth', type: 'feature' as const },
            { text: 'Sign in with Discord OAuth', type: 'feature' as const },
            { text: 'Social login buttons added to AuthModal popup', type: 'feature' as const },
          ]
        },
        {
          icon: '📝',
          title: 'Blog System Improvements',
          color: '#f59e0b',
          items: [
            { text: 'Complete English translations for all 82 articles (100% coverage)', type: 'feature' as const },
            { text: 'Articles now open on showcase site (edouard.ai) for better SEO', type: 'improvement' as const },
            { text: 'Random article order on homepage for variety', type: 'improvement' as const },
            { text: 'Table of contents with proper heading ID anchors', type: 'fix' as const },
            { text: 'Category-prefixed links rewritten to /blog/ path', type: 'fix' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Technical Improvements',
          color: '#ef4444',
          items: [
            { text: 'Added `style` prop support to Search and Filter icons', type: 'fix' as const },
            { text: 'Removed unnecessary React.lazy/Suspense lazy loading', type: 'improvement' as const },
          ]
        },
      ]
    },
    {
      version: '6.6',
      title: 'Blog System, FluxNode Alerts & Congress Trading',
      date: '4 Feb 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '📝',
          title: 'Blog System & CMS',
          color: '#8b5cf6',
          items: [
            { text: 'Complete blog system with admin CMS panel', type: 'feature' as const },
            { text: 'Import of 117 articles from markdown files', type: 'feature' as const },
            { text: 'Multilingual support (FR/EN) for all articles', type: 'feature' as const },
            { text: 'Categories, tags, and SEO optimization', type: 'feature' as const },
            { text: 'Supabase Storage integration for media uploads', type: 'feature' as const },
            { text: 'Article editor with live preview', type: 'feature' as const },
          ]
        },
        {
          icon: '🔔',
          title: 'FluxNode Monitoring Alerts',
          color: '#10b981',
          items: [
            { text: 'Email notifications when nodes go down or need maintenance', type: 'feature' as const },
            { text: 'PNR (Progressive Node Rewards) eligibility display on node cards', type: 'feature' as const },
            { text: 'Blockchain health history with automated cron collection', type: 'feature' as const },
            { text: 'Alert resolution system with automatic status updates', type: 'feature' as const },
            { text: 'FluxNode Apps monitoring with owner sync to Supabase', type: 'feature' as const },
          ]
        },
        {
          icon: '🏛️',
          title: 'Congress Trading Tracker',
          color: '#f59e0b',
          items: [
            { text: 'New page to track US Congress members stock trades (STOCK Act)', type: 'feature' as const },
            { text: 'FMP API integration for disclosure data', type: 'feature' as const },
            { text: 'Professional UI with filters and search', type: 'feature' as const },
            { text: 'Authentication required for access', type: 'security' as const },
          ]
        },
        {
          icon: '🌐',
          title: 'Showcase Website (edouard.ai)',
          color: '#3b82f6',
          items: [
            { text: 'Complete showcase site built with Next.js 15', type: 'feature' as const },
            { text: 'Dedicated pages for all 11 monitors and features', type: 'feature' as const },
            { text: 'Multilingual support (FR/EN) with next-intl', type: 'feature' as const },
            { text: 'Blog section connected to main app database', type: 'feature' as const },
            { text: 'Contact form with API integration', type: 'feature' as const },
            { text: 'PWA promotion for app installation', type: 'feature' as const },
          ]
        },
        {
          icon: '🤖',
          title: 'Discord Bot',
          color: '#5865F2',
          items: [
            { text: 'FluxNode monitoring bot for Discord servers', type: 'feature' as const },
            { text: 'Render Blueprint deployment configuration', type: 'feature' as const },
            { text: 'Real-time node status notifications', type: 'feature' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Bug Fixes & Improvements',
          color: '#ef4444',
          items: [
            { text: 'Fixed auth race condition causing data not to load on refresh', type: 'fix' as const },
            { text: 'Fixed infinite loading spinner on page refresh', type: 'fix' as const },
            { text: 'Fixed FluxNode alerts not showing in frontend', type: 'fix' as const },
            { text: 'Fixed FluxNode app expiration alerts - now only triggers when < 7 days remaining', type: 'fix' as const },
            { text: 'Improved loading state sync in PortfolioDashboard', type: 'fix' as const },
            { text: 'Settings page redesign for better UX', type: 'improvement' as const },
            { text: 'Updated FluxNode hosting prices', type: 'improvement' as const },
          ]
        },
      ]
    },
    {
      version: '6.5',
      title: 'New Monitors (Algorand, Cardano, XDC, VeChain, ICP, Axelar)',
      date: '4 Jan 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🔗',
          title: 'New Blockchain Monitors',
          color: '#10b981',
          items: [
            { text: 'Algorand Monitor: Track ALGO accounts, ASA tokens and staking rewards', type: 'feature' as const },
            { text: 'Cardano Monitor: Track ADA accounts, staking pools and delegation rewards', type: 'feature' as const },
            { text: 'XDC Monitor: Track XDC Network accounts and masternodes', type: 'feature' as const },
            { text: 'VeChain Monitor: Track VET/VTHO accounts with VTHO generation calculator', type: 'feature' as const },
            { text: 'ICP Monitor: Track Internet Computer accounts, neurons and staking', type: 'feature' as const },
            { text: 'Axelar Monitor: Track AXL accounts, cross-chain activity and staking', type: 'feature' as const },
            { text: 'All new monitors disabled by default in ModuleHub (opt-in)', type: 'improvement' as const },
          ]
        },
        {
          icon: '🌐',
          title: 'API Integrations',
          color: '#6747ED',
          items: [
            { text: 'Algonode API for Algorand (no API key required)', type: 'feature' as const },
            { text: 'Blockfrost API for Cardano (50k req/day free tier)', type: 'feature' as const },
            { text: 'XDCScan API + RPC for XDC Network', type: 'feature' as const },
            { text: 'VeChain Thor public API for VET/VTHO', type: 'feature' as const },
            { text: 'ICP Rosetta API for Internet Computer', type: 'feature' as const },
            { text: 'Axelar LCD API (Cosmos SDK) + Axelarscan for cross-chain data', type: 'feature' as const },
          ]
        },
        {
          icon: '💰',
          title: 'Staking & Rewards',
          color: '#f59e0b',
          items: [
            { text: 'Cardano: Stake pool delegation with APY and rewards tracking', type: 'feature' as const },
            { text: 'VeChain: VTHO generation rate calculator (0.000432 VTHO/VET/day)', type: 'feature' as const },
            { text: 'ICP: Neuron staking with dissolve delay and voting power', type: 'feature' as const },
            { text: 'Algorand: Participation rewards and governance tracking', type: 'feature' as const },
            { text: 'Axelar: Validator delegation with APR and rewards display', type: 'feature' as const },
          ]
        },
        {
          icon: '🎨',
          title: 'UI & Icons',
          color: '#3b82f6',
          items: [
            { text: 'Official blockchain icons for all new monitors', type: 'feature' as const },
            { text: 'New badge on all new monitor pages in sidebar', type: 'improvement' as const },
            { text: 'Consistent monitor page layout matching existing monitors', type: 'improvement' as const },
            { text: 'Axelar: Supported chains tab showing 40+ connected blockchains', type: 'feature' as const },
          ]
        },
      ]
    },
    {
      version: '6.4',
      title: 'The Graph Monitor & Analytics Cleanup',
      date: '3 Jan 2026',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '📊',
          title: 'The Graph (GRT) Monitor',
          color: '#6747ED',
          items: [
            { text: 'New dedicated page to monitor GRT delegations', type: 'feature' as const },
            { text: 'Track multiple wallet addresses with delegation data', type: 'feature' as const },
            { text: 'View delegated amount, pending rewards, and realized rewards per wallet', type: 'feature' as const },
            { text: 'Delegations tab with indexer details (name, reward cut, query fee cut)', type: 'feature' as const },
            { text: 'External link to open indexer profile on The Graph Explorer', type: 'feature' as const },
            { text: 'Estimated APY calculation based on network issuance and indexer cuts', type: 'feature' as const },
            { text: 'Network stats tab with total supply, staked tokens, and epoch info', type: 'feature' as const },
            { text: 'Top indexers list sorted by delegated tokens', type: 'feature' as const },
            { text: 'GRT price from CoinGecko with USD value display', type: 'feature' as const },
            { text: 'Auto-reinvested note for realized rewards (rewards are compounded)', type: 'improvement' as const },
          ]
        },
        {
          icon: '🧹',
          title: 'Analytics Cleanup',
          color: '#ef4444',
          items: [
            { text: 'Removed Plausible.io Analytics tracking code', type: 'improvement' as const },
            { text: 'Cleaned up Content Security Policy (removed plausible.io domains)', type: 'improvement' as const },
            { text: 'Deleted analytics service file for lighter bundle', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Technical',
          color: '#3b82f6',
          items: [
            { text: 'The Graph Network Subgraph integration via GraphQL', type: 'feature' as const },
            { text: 'API key configuration via `VITE_THEGRAPH_API_KEY` environment variable', type: 'feature' as const },
            { text: 'Decentralized gateway endpoint (gateway-arbitrum.network.thegraph.com)', type: 'feature' as const },
            { text: 'TypeScript types for Delegator, Indexer, Stake, and Network entities', type: 'feature' as const },
          ]
        },
      ]
    },
    {
      version: '6.3',
      title: 'Investment Advisor, Recovery Simulator & Cloud Sync',
      date: '30 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🤖',
          title: 'Investment Advisor (Edouard AI)',
          color: '#8b5cf6',
          items: [
            { text: 'New dedicated Investment Advisor page with AI chatbot', type: 'feature' as const },
            { text: 'Sidebar mode for large screens (>= 1400px) with collapsible panel', type: 'feature' as const },
            { text: 'Full mobile responsive design for iPhone/Android', type: 'feature' as const },
            { text: 'Supabase storage for conversation history (synced across devices)', type: 'feature' as const },
            { text: 'Edouard AI branding on chat panel and floating button', type: 'improvement' as const },
            { text: 'Multilingual responses based on user\'s language setting', type: 'feature' as const },
            { text: 'Claude AI as primary with fallback to Gemini/Mistral', type: 'feature' as const },
            { text: 'Restricted access to authenticated users only', type: 'security' as const },
          ]
        },
        {
          icon: '📊',
          title: 'Investment Recovery Simulator',
          color: '#10b981',
          items: [
            { text: 'New "Simulator" button in Portfolio toolbar (Expert mode)', type: 'feature' as const },
            { text: 'Simulate investing X$ on each token and see impact of Y% price increase', type: 'feature' as const },
            { text: 'Efficiency score algorithm to find best recovery opportunities', type: 'feature' as const },
            { text: 'Identifies tokens that would flip from loss to profit', type: 'feature' as const },
            { text: 'Configurable investment amount and target rise percentage', type: 'feature' as const },
            { text: 'Filter to show only tokens currently in loss', type: 'feature' as const },
            { text: 'Rankings by recovery potential to guide investment decisions', type: 'feature' as const },
          ]
        },
        {
          icon: '☁️',
          title: 'Cloud Sync & Data Persistence',
          color: '#3b82f6',
          items: [
            { text: 'Clear all local cache on logout (preserving language, theme, remember-me)', type: 'feature' as const },
            { text: 'React Query cache cleared on logout for clean state', type: 'feature' as const },
            { text: 'Monitor wallets (FluxNode, Cronos, Hedera) cleared on logout', type: 'feature' as const },
            { text: 'Portfolio snapshots synced with Supabase database', type: 'feature' as const },
            { text: 'User preferences (userMode, showBTCComparison) synced with Supabase', type: 'feature' as const },
            { text: 'Use local data first when Supabase times out', type: 'fix' as const },
            { text: 'Fallback to local "default" portfolio ID when cloud ID not found', type: 'fix' as const },
          ]
        },
        {
          icon: '🔐',
          title: 'Authentication Improvements',
          color: '#ec4899',
          items: [
            { text: 'Fixed "Remember Me" checkbox not saving email on login', type: 'fix' as const },
            { text: 'Persist user profile data (avatar, displayName) after page refresh', type: 'fix' as const },
            { text: 'Fixed auth refresh issues for better session handling', type: 'fix' as const },
          ]
        },
        {
          icon: '📈',
          title: 'Analytics & Monitoring',
          color: '#06b6d4',
          items: [
            { text: 'Vercel Analytics integration for usage tracking', type: 'feature' as const },
            { text: 'Vercel Speed Insights for performance monitoring', type: 'feature' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'UI/UX Improvements',
          color: '#f59e0b',
          items: [
            { text: 'iPad Dashboard sparklines opacity increased (0.15 → 0.25)', type: 'improvement' as const },
            { text: 'iPad Dashboard token card border opacity reduced for cleaner look', type: 'improvement' as const },
            { text: 'Auto-sync monitor widget configs from cloud after login', type: 'improvement' as const },
            { text: 'Make monitor widgets refresh after cloud sync', type: 'improvement' as const },
            { text: 'Removed tooltip from alert bell icon for cleaner UI', type: 'improvement' as const },
            { text: 'Fixed portfolio alerts not triggering correctly', type: 'fix' as const },
          ]
        },
      ]
    },
    {
      version: '6.2',
      title: 'iOS Native App & UI/UX Accessibility',
      date: '28 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '📱',
          title: 'iOS Native Application',
          color: '#007AFF',
          items: [
            { text: 'Complete SwiftUI native iOS app for iPhone', type: 'feature' as const },
            { text: 'Supabase authentication (Email/Password + Sign in with Apple)', type: 'feature' as const },
            { text: 'Home screen with live widget cards (FluxNodes, CRO, HBAR)', type: 'feature' as const },
            { text: 'Portfolio view with performance chart and token list', type: 'feature' as const },
            { text: 'Nodes Hub for unified staking management', type: 'feature' as const },
            { text: 'Monitor detail views (FluxNode, Hedera, Cronos)', type: 'feature' as const },
            { text: 'WidgetKit extension with Portfolio, FluxNodes, Staking widgets', type: 'feature' as const },
            { text: 'Lock Screen widget support (iOS 16+)', type: 'feature' as const },
            { text: 'Onboarding flow with feature presentation', type: 'feature' as const },
            { text: 'Settings with wallet configuration and preferences', type: 'feature' as const },
            { text: 'Session persistence with Keychain storage', type: 'security' as const },
          ]
        },
        {
          icon: '🔌',
          title: 'API Endpoints for iOS',
          color: '#10b981',
          items: [
            { text: '`/api/v1/widget/summary` - Single optimized endpoint for iOS widgets', type: 'feature' as const },
            { text: '`/api/v1/monitor/wallets` - CRUD for monitored wallets', type: 'feature' as const },
            { text: '`/api/v1/portfolio/snapshots` - Historical portfolio values', type: 'feature' as const },
            { text: '`/api/v1/alerts` - Price alerts CRUD operations', type: 'feature' as const },
            { text: '`/api/v1/prices/history` - Token price history', type: 'feature' as const },
            { text: '`/api/v1/nodes/flux/rewards` - FluxNode rewards history', type: 'feature' as const },
            { text: '`/api/v1/hosting/subscriptions` - Hosting subscriptions management', type: 'feature' as const },
          ]
        },
        {
          icon: '♿',
          title: 'Accessibility (WCAG AA)',
          color: '#8b5cf6',
          items: [
            { text: 'WCAG AA color contrast compliance across all components', type: 'improvement' as const },
            { text: 'Skip-link for keyboard navigation ("Skip to main content")', type: 'feature' as const },
            { text: '`aria-current="page"` on active navigation items', type: 'improvement' as const },
            { text: 'ARIA labels on SwipeableRow and interactive elements', type: 'improvement' as const },
            { text: 'Automated accessibility tests with axe-core', type: 'feature' as const },
            { text: 'Reduced motion support for users with vestibular disorders', type: 'improvement' as const },
          ]
        },
        {
          icon: '🎨',
          title: 'UI/UX Improvements',
          color: '#3b82f6',
          items: [
            { text: 'Unified Button component with loading states and variants', type: 'feature' as const },
            { text: 'Unified Input component with validation and error states', type: 'feature' as const },
            { text: 'Breadcrumb component for navigation hierarchy', type: 'feature' as const },
            { text: 'Tabs component with accessible keyboard navigation', type: 'feature' as const },
            { text: 'ResponsiveTable component with mobile-friendly layout', type: 'feature' as const },
            { text: '`useMediaQuery` hook for responsive breakpoints', type: 'feature' as const },
            { text: 'Tablet breakpoint support (768-1024px)', type: 'improvement' as const },
            { text: 'Toast notifications replacing native alert/confirm', type: 'improvement' as const },
            { text: 'ConfirmationModal for destructive actions', type: 'feature' as const },
            { text: 'AutoFocus on primary form inputs', type: 'improvement' as const },
            { text: 'Keyboard shortcut tooltips on hover', type: 'improvement' as const },
            { text: 'Button loading states with spinners', type: 'improvement' as const },
            { text: 'Haptic feedback hook for touch interactions', type: 'feature' as const },
          ]
        },
        {
          icon: '🖥️',
          title: 'FluxNode Enhancements',
          color: '#f59e0b',
          items: [
            { text: 'Accumulated rewards display (balance - collateral)', type: 'feature' as const },
            { text: 'Parallel Assets claimable tokens in rewards section', type: 'feature' as const },
            { text: 'Accumulated rewards in chart header', type: 'feature' as const },
            { text: 'Accumulated rewards in FluxNode widget card', type: 'feature' as const },
            { text: 'Widget rewards style matching HBAR staking', type: 'improvement' as const },
            { text: 'Improved Parallel Assets API parsing with better error handling', type: 'fix' as const },
            { text: 'Removed Recent Transactions table from dashboard', type: 'fix' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Onboarding & Architecture',
          color: '#ec4899',
          items: [
            { text: 'Consolidated onboarding from 9 steps to 5 essential steps', type: 'improvement' as const },
            { text: 'iOS native app roadmap documentation', type: 'feature' as const },
          ]
        },
      ]
    },
    {
      version: '6.1',
      title: 'HomePage Dashboard & ModuleHub Redesign',
      date: '23 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🏠',
          title: 'New HomePage Dashboard',
          color: '#8b5cf6',
          items: [
            { text: 'Complete HomePage redesign with service widgets', type: 'feature' as const },
            { text: 'Glass morphism KPI cards (Total Value, P/L, 24h Change, Best Performer)', type: 'feature' as const },
            { text: 'Rotating AI insights with portfolio analysis messages', type: 'feature' as const },
            { text: 'Quick Actions: Modules, Portfolio, Bitcoin Cycles, Monitors', type: 'feature' as const },
            { text: 'FluxNode, Hedera, Cronos widgets with live status', type: 'feature' as const },
            { text: 'Latest blog articles from WordPress API', type: 'feature' as const },
            { text: 'Personalized greeting with username if logged in', type: 'feature' as const },
            { text: 'Full i18n support (French/English)', type: 'feature' as const },
            { text: 'Click on sidebar logo navigates to Home', type: 'improvement' as const },
          ]
        },
        {
          icon: '🎨',
          title: 'ModuleHub Redesign',
          color: '#10b981',
          items: [
            { text: 'Skeleton widget style for unconfigured modules (dashed borders)', type: 'feature' as const },
            { text: 'Gradient toggle switches with shadow when enabled', type: 'improvement' as const },
            { text: 'Replaced "Ouvrir" button with simple text link', type: 'improvement' as const },
            { text: 'Reduced toggle switch size on mobile', type: 'fix' as const },
          ]
        },
        {
          icon: '📱',
          title: 'Sidebar Improvements',
          color: '#3b82f6',
          items: [
            { text: 'Submenus closed by default, open on hover', type: 'improvement' as const },
            { text: 'Click to toggle submenu open/closed permanently', type: 'improvement' as const },
            { text: 'Hedera Monitor enabled by default', type: 'feature' as const },
          ]
        },
        {
          icon: '🔧',
          title: 'Bug Fixes',
          color: '#ef4444',
          items: [
            { text: 'Fixed browser back button infinite loop navigation issue', type: 'fix' as const },
            { text: 'Fixed toast notification height stretching on mobile', type: 'fix' as const },
            { text: 'Fixed `featureInfo` undefined error in FeatureTooltip', type: 'fix' as const },
            { text: 'Fixed undefined `ArrowUpRight`/`ArrowDownRight` icons', type: 'fix' as const },
            { text: 'Added null safety to dashboard widgets', type: 'fix' as const },
          ]
        },
        {
          icon: '🧪',
          title: 'Testing & Quality',
          color: '#f59e0b',
          items: [
            { text: '817+ tests passing with Vitest', type: 'improvement' as const },
            { text: 'Playwright E2E test infrastructure', type: 'feature' as const },
            { text: 'Feature discovery micro-onboarding system', type: 'feature' as const },
          ]
        },
      ]
    },
    {
      version: '6.0',
      title: t('changelog.v60.title', 'AI Consensus Bot Auth & Portfolio Charts'),
      date: '19 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'major',
      sections: [
        {
          icon: '🤖',
          title: t('changelog.v60.aiBot.title', 'AI Consensus Bot Authentication'),
          color: '#8b5cf6',
          items: [
            { text: 'JWT-based user authentication system for AI Consensus Bot', type: 'feature' as const },
            { text: 'Login/Register modal with email & password', type: 'feature' as const },
            { text: 'AuthContext provider with `useAuth` hook for token management', type: 'feature' as const },
            { text: 'Auto-include JWT token in all API requests via `getAuthHeaders()`', type: 'feature' as const },
            { text: 'User session persistence with localStorage', type: 'feature' as const },
            { text: 'Login/Logout buttons in AI Consensus Bot header', type: 'feature' as const },
          ]
        },
        {
          icon: '🔑',
          title: t('changelog.v60.exchanges.title', 'Exchange API Keys Management'),
          color: '#f59e0b',
          items: [
            { text: 'New "Exchanges" tab for managing exchange credentials per user', type: 'feature' as const },
            { text: 'Add/Remove Binance API keys with testnet toggle', type: 'feature' as const },
            { text: 'Test connection button to verify API key validity', type: 'feature' as const },
            { text: 'Show account balances after successful connection', type: 'feature' as const },
            { text: 'AES-256-GCM encryption for secure credential storage', type: 'security' as const },
            { text: 'User-specific API keys instead of global environment variables', type: 'improvement' as const },
          ]
        },
        {
          icon: '📈',
          title: t('changelog.v60.charts.title', 'Portfolio Chart Transaction Markers'),
          color: '#10b981',
          items: [
            { text: 'Buy/Sell/Swap markers on TradingView price chart', type: 'feature' as const },
            { text: 'Green arrow up for buys, red arrow down for sells', type: 'feature' as const },
            { text: 'Purple circle for swap IN, orange circle for swap OUT', type: 'feature' as const },
            { text: 'Blue square for mining, cyan square for staking', type: 'feature' as const },
            { text: '"Transactions" toggle button to show/hide markers', type: 'feature' as const },
            { text: 'Transaction legend with marker type explanations', type: 'feature' as const },
            { text: 'Hover tooltip showing quantity and price on each marker', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔧',
          title: t('changelog.v60.fixes.title', 'Bug Fixes'),
          color: '#ef4444',
          items: [
            { text: 'Fixed CoinGecko chart data interval for periods > 90 days', type: 'fix' as const },
            { text: 'Made ChartDataSchema more lenient for optional fields', type: 'fix' as const },
            { text: 'Added missing `LogIn` and `LogOut` icons to Icons component', type: 'fix' as const },
            { text: 'Added debug logging for price chart data troubleshooting', type: 'improvement' as const },
          ]
        },
      ]
    },
    {
      version: '5.9',
      title: t('changelog.v59.title', 'Cronos Masternode & Monitor Menu'),
      date: '16 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🎯',
          title: t('changelog.v59.masternode.title', 'Cronos Masternode Waitlist'),
          color: '#FFD700',
          items: [
            { text: 'Masternode waitlist signup form with email + CRO wallet address', type: 'feature' as const },
            { text: 'Progress bar showing 800,000 / 3,500,000 CRO goal (22.86%)', type: 'feature' as const },
            { text: 'Early Bird Supporters section with VIP badge', type: 'feature' as const },
            { text: 'Admin notification email on new signup', type: 'feature' as const },
            { text: 'VIP confirmation email to customer (multilingual FR/EN/ES/PT/HI)', type: 'feature' as const },
            { text: 'localStorage persistence for Early Bird list', type: 'feature' as const },
            { text: 'Removed fake Recent Winners section', type: 'fix' as const },
          ]
        },
        {
          icon: '📂',
          title: t('changelog.v59.sidebar.title', 'Sidebar Monitor Menu'),
          color: '#8b5cf6',
          items: [
            { text: 'New "Monitor" menu grouping FluxNode, Hedera, Cronos, Wallet Tracker', type: 'feature' as const },
            { text: 'Sub-pages visible when collapsed (only for active menu)', type: 'improvement' as const },
            { text: 'Transparent background for sub-pages (visual hierarchy)', type: 'improvement' as const },
            { text: 'Active sub-page at full opacity, others at 50%', type: 'improvement' as const },
            { text: 'Cronos Staking enabled by default', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔔',
          title: t('changelog.v59.discovery.title', 'Token Discovery Popups'),
          color: '#10b981',
          items: [
            { text: 'Contextual slide-in notification when HBAR/FLUX/CRO detected in portfolio', type: 'feature' as const },
            { text: 'Auto-enable corresponding Monitor module when token detected', type: 'feature' as const },
            { text: '"Explore" button navigates to Monitor page', type: 'feature' as const },
            { text: '30-day dismiss memory per token', type: 'improvement' as const },
            { text: 'Fixed undefined token symbol error (toUpperCase)', type: 'fix' as const },
          ]
        },
        {
          icon: '🌍',
          title: t('changelog.v59.i18n.title', 'Internationalization'),
          color: '#3b82f6',
          items: [
            { text: 'Masternode translations added for 5 languages (FR/EN/ES/PT/HI)', type: 'feature' as const },
            { text: 'Monitor menu label translations', type: 'improvement' as const },
          ]
        },
        {
          icon: '🔧',
          title: t('changelog.v59.fixes.title', 'Bug Fixes'),
          color: '#ef4444',
          items: [
            { text: 'Fixed email sending using `app.edouard.ai` subdomain (verified in Resend)', type: 'fix' as const },
            { text: 'Fixed TokenDiscoveryPopup undefined token symbols', type: 'fix' as const },
            { text: 'Fixed sidebar Icons.Monitor not found (changed to Icons.Eye)', type: 'fix' as const },
          ]
        },
      ]
    },
    {
      version: '5.8',
      title: t('changelog.v58.title', 'UI Redesign & Module Hub Modernization'),
      date: '15 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🎨',
          title: t('changelog.v58.moduleHub.title', 'ModuleHub Redesign'),
          color: '#8b5cf6',
          items: (t('changelog.v58.moduleHub.items', { returnObjects: true, defaultValue: [
            'Complete redesign with glass-morphism and unified styling',
            'New search functionality to filter modules',
            'Compact module cards with hover effects',
            'Modern toggle switches and badges',
            'Category headers with gradient icons',
            'Responsive grid layout',
            'Exchanges category moved after Community'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '📊',
          title: t('changelog.v58.charts.title', 'Charts & Visualizations'),
          color: '#10b981',
          items: (t('changelog.v58.charts.items', { returnObjects: true, defaultValue: [
            'Redesign OriginPerformanceChart with unified styling',
            'Added vertical indicator line on chart hover',
            'Fixed grayish hover overlay on chart rows',
            'Modernized SwapChainVisualization with sparklines',
            'Added mini sparklines to swap steps'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '📱',
          title: t('changelog.v58.dashboardIpad.title', 'Dashboard iPad'),
          color: '#3b82f6',
          items: (t('changelog.v58.dashboardIpad.items', { returnObjects: true, defaultValue: [
            'Complete redesign with unified styling',
            'Added sparklines to token cards',
            'Removed token limits for unlimited tokens',
            'Default grid changed to 3x4',
            'Removed initials placeholder from cards',
            'Lighter sparkline stroke effect'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🔔',
          title: t('changelog.v58.alerts.title', 'Alerts System'),
          color: '#f59e0b',
          items: (t('changelog.v58.alerts.items', { returnObjects: true, defaultValue: [
            'Redesigned AlertBell and AlertPanel with unified look',
            'Modernized AlertForm modal with glass-morphism',
            'Fixed preservation of repeatable alerts when clearing triggered',
            'Fixed missing ArrowUp/ArrowDown icons'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🧾',
          title: t('changelog.v58.tax.title', 'Tax Center Consolidation'),
          color: '#ec4899',
          items: (t('changelog.v58.tax.items', { returnObjects: true, defaultValue: [
            'Merged Tax Guide, Tax Center, and Tax Loss Harvesting into single module',
            'One toggle in ModuleHub controls all 3 tax pages',
            'Added linkedPages property for module grouping'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🍞',
          title: t('changelog.v58.toast.title', 'Toast Notifications'),
          color: '#06b6d4',
          items: (t('changelog.v58.toast.items', { returnObjects: true, defaultValue: [
            'Redesigned with glass-morphism effect',
            'Compact padding and smaller icons',
            'Modern unified styling with CSS variables'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🔧',
          title: t('changelog.v58.fixes.title', 'Bug Fixes'),
          color: '#ef4444',
          items: (t('changelog.v58.fixes.items', { returnObjects: true, defaultValue: [
            'Fixed missing icons in SwapChainVisualization (ArrowDown, Link)',
            'Fixed undefined token.symbol errors in TechnicalAnalysisDashboard',
            'Added defensive checks for undefined token.symbol in 4 components',
            'Removed hover effect from chart/visualization table rows',
            'Temporarily disabled Voice Command functionality',
            'Re-enabled Hosting Rig tab in FluxNode'
          ] }) as string[]).map(text => ({ text, type: 'fix' as const }))
        },
        {
          icon: '🎰',
          title: t('changelog.v58.cronosLotto.title', 'Cronos Lotto'),
          color: '#FFD700',
          items: (t('changelog.v58.cronosLotto.items', { returnObjects: true, defaultValue: [
            'New Lotto tab with no-loss lottery system',
            'Delegate CRO and automatically participate in daily draws',
            '50 winners per day receive 30% of staking gains',
            'Animated Loaded Lion SVG mascot',
            'Lotto bonus widget on Staking tab showing potential gains',
            'Earnings simulator with delegation amount slider',
            'Animated CRO coins treasure SVG',
            'Hetzner partner infrastructure badge',
            'Detailed "How it works" explanation section',
            'Full mobile/iPhone responsive design',
            'Promotional popup on staking tab',
            'Translations in 5 languages (FR, EN, ES, PT, HI)'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        }
      ]
    },
    {
      version: '5.7',
      title: t('changelog.v57.title', 'FluxNode Email Automation & Portfolio Insights'),
      date: '14 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '📧',
          title: t('changelog.v57.email.title', 'Automated Quote Email System'),
          color: '#8b5cf6',
          items: (t('changelog.v57.email.items', { returnObjects: true, defaultValue: [
            'Automatic quote email sent to customer on FluxNode hosting request',
            'Multi-language support for emails (French, English, Portuguese)',
            'Professional HTML email with pricing table and node configuration',
            'CTA button linking to Datavex partner offers',
            'Wallet addresses included in quote for easy reference',
            'Multiple CC recipients support (configurable via env var)',
            'Correct partner pricing: Cumulus 7.99€, Nimbus 19.99€, Stratus 39.99€'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🍩',
          title: t('changelog.v57.donut.title', 'Portfolio Donut Enhanced'),
          color: '#10b981',
          items: (t('changelog.v57.donut.items', { returnObjects: true, defaultValue: [
            'Profit/loss indicator ring with gradient color (red → yellow → green)',
            'Animated pulse effect on outer ring for visual attention',
            'Inner ring shows hovered token gain/loss status',
            'Tooltip displays gain/loss %, amount, and cost basis per token',
            'Legend shows P/L percentage for each token with colored indicators',
            'Portfolio summary stats: invested → current value with total P/L',
            'P/L percentage displayed in center of donut chart'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '📱',
          title: t('changelog.v57.mobile.title', 'Mobile & Responsive'),
          color: '#3b82f6',
          items: (t('changelog.v57.mobile.items', { returnObjects: true, defaultValue: [
            'Token Risk Analysis page fully responsive for iPhone/mobile',
            'Metrics grid: 2 columns on mobile, 4 columns on desktop',
            'Compact technical indicators with 2-column mobile layout',
            'Positions tables with horizontal scroll on small screens',
            'Header layout stacked and flexible on mobile devices'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🔧',
          title: t('changelog.v57.fixes.title', 'Bug Fixes'),
          color: '#ef4444',
          items: (t('changelog.v57.fixes.items', { returnObjects: true, defaultValue: [
            'Fixed React Error 130: Added missing `BarChart2` icon component',
            'Fixed hosting prices calculation using correct partner rates',
            'Email sender updated to "Edouard.ai Solutions" branding',
            'Replaced "Datavex Partner" with "Official Partner" badge'
          ] }) as string[]).map(text => ({ text, type: 'fix' as const }))
        },
        {
          icon: '🖥️',
          title: t('changelog.v57.hostingRig.title', 'Hosting Rig Management'),
          color: '#ec4899',
          items: (t('changelog.v57.hostingRig.items', { returnObjects: true, defaultValue: [
            'New "Hosting Rig" tab in FluxNode page for complete hosting administration',
            'Unified UI with compact pricing cards (Cumulus 7.99€, Nimbus 19.99€, Stratus 39.99€)',
            'Two subscription modes: Manual entry or blockchain import from existing wallets',
            'Multi-node subscription: add multiple nodes in a single request',
            'Real-time reward calculations from live FluxNode API data',
            'Import feature to select unhosted nodes from tracked wallets',
            'Managed nodes section with status tracking (pending, active, expired)',
            'Official FluxNode specifications: Cumulus 2C/8GB/220GB, Nimbus 4C/32GB/440GB, Stratus 8C/64GB/880GB',
            'Full i18n support (FR, EN, ES, PT, HI) with improved contrast/readability'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        }
      ]
    },
    {
      version: '5.6',
      title: t('changelog.v56.title', 'Dashboard Performance & Premium Animations'),
      date: '14 Dec 2025',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '⚡',
          title: t('changelog.v56.performance.title', 'Performance Optimizations'),
          color: '#10b981',
          items: (t('changelog.v56.performance.items', { returnObjects: true, defaultValue: [
            'Added `React.memo` to 5 dashboard components (MetricCard, Donut, Chart, Suggestions, Header)',
            'Wrapped 18+ handlers with `useCallback` for stable references',
            'Extracted widget building into `useMemo` to prevent rebuild on every render',
            'Mouse tracking optimized with `useRef` instead of `useState`',
            'Created `useDashboardReducer` hook to consolidate 28 state variables',
            'Lazy loading for heavy chart components (SpreadChart, Donut, Heatmap)'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '✨',
          title: t('changelog.v56.animations.title', 'Premium Micro-Interactions'),
          color: '#8b5cf6',
          items: (t('changelog.v56.animations.items', { returnObjects: true, defaultValue: [
            'Card entrance animations with staggered fade-in + slide-up',
            'Sparkline draw animation (stroke-dashoffset effect)',
            'Value pulse animation on price changes (green for up, red for down)',
            'Card glow effect on pulse with colored border highlight',
            'Skeleton shimmer animation for loading states',
            'Smooth hover transitions with border color change'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '📊',
          title: t('changelog.v56.header.title', 'Dashboard Header Redesign'),
          color: '#3b82f6',
          items: (t('changelog.v56.header.items', { returnObjects: true, defaultValue: [
            'New header layout with metric cards, donut chart, and value chart',
            'Customizable widget grid layout with drag-and-drop reordering',
            'Mini portfolio donut with interactive tooltips and click-to-expand',
            'Portfolio value evolution chart with BTC price overlay (orange dashed line)',
            'Compact smart suggestions panel with detailed actionable insights',
            'Total invested value displayed below current portfolio value',
            '"API pro" green indicator replacing "Live" badge'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🖥️',
          title: t('changelog.v56.fluxnode.title', 'FluxNode Hosting Partnership'),
          color: '#ec4899',
          items: (t('changelog.v56.fluxnode.items', { returnObjects: true, defaultValue: [
            'New Hosting Promo Popup with Datavex partner pricing',
            'Email quote request for FluxNode hosting services',
            'Daily/monthly price display from partner API',
            'i18n translations for hosting features (5 languages)',
            'Popup dismiss preference saved for 7 days'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🔧',
          title: t('changelog.v56.technical.title', 'Technical Improvements'),
          color: '#f59e0b',
          items: (t('changelog.v56.technical.items', { returnObjects: true, defaultValue: [
            'Dashboard layout included in JSON backup/export system',
            'Risk Analysis & ATH components added to Risk page',
            'Added Portuguese (Brazilian) language support (6th language)',
            'New translations for chart titles and sync labels (FR/EN)',
            'Fixed React Error #31 (priceSource rendering)',
            'Fixed widget/children array mismatch in edit mode'
          ] }) as string[]).map(text => ({ text, type: 'fix' as const }))
        }
      ]
    },
    {
      version: '5.5',
      title: t('changelog.v55.title', 'Micro-Interactions & Celebrations'),
      date: '9 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🎉',
          title: t('changelog.v55.celebrations.title', 'Celebration System'),
          color: '#f59e0b',
          items: (t('changelog.v55.celebrations.items', { returnObjects: true, defaultValue: [
            'Confetti animation when portfolio reaches new All-Time High',
            'Fireworks with percentage display on significant gains (+5%)',
            'Rocket animation when crossing from loss to profit',
            'Target Reached celebration when price alerts trigger',
            'Individual token ATH detection with confetti'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '✨',
          title: t('changelog.v55.animations.title', 'Animation Library'),
          color: '#8b5cf6',
          items: (t('changelog.v55.animations.items', { returnObjects: true, defaultValue: [
            'New `micro-interactions` component library inspired by Rive.app',
            'AnimatedNumber, AnimatedCurrency, AnimatedPercentage components',
            'TiltCard, HoverLiftCard, GlowCard, FlipCard components',
            'StaggeredList, CascadingGrid, AnimatedAccordion components',
            'RippleButton, MagneticButton, IconButton components',
            'ScrollReveal, ParallaxSection, ScrollProgressBar components'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🎯',
          title: t('changelog.v55.integration.title', 'Smart Integration'),
          color: '#10b981',
          items: (t('changelog.v55.integration.items', { returnObjects: true, defaultValue: [
            'CelebrationProvider context for global celebration triggers',
            'useCelebrations hook for easy access from any component',
            'usePriceChangeDetector hook for automatic gain detection',
            'AlertService callback system for price target celebrations',
            'Portfolio ATH persistence in localStorage',
            'Cooldown system to prevent celebration spam'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🎮',
          title: t('changelog.v55.demo.title', 'Demo Page'),
          color: '#3b82f6',
          items: (t('changelog.v55.demo.items', { returnObjects: true, defaultValue: [
            'New `/demo` page to test all micro-interactions',
            'Interactive buttons to trigger each celebration type',
            'Showcase of all animated components'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        }
      ]
    },
    {
      version: '5.4',
      title: t('changelog.v54.title', 'Multilingual Support & 4 Languages'),
      date: '9 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🌍',
          title: t('changelog.v54.i18n.title', 'Internationalization'),
          color: '#10b981',
          items: (t('changelog.v54.i18n.items', { returnObjects: true, defaultValue: [
            'Added Spanish (ES) and Hindi (HI) language support',
            'New language dropdown with flags (FR/EN/ES/HI)',
            'Browser language auto-detection with manual override',
            'Documentation.tsx fully converted to i18n (100+ keys)',
            'GettingStarted.tsx fully converted to i18n (50+ keys)',
            'PositionModal.tsx fully converted to i18n (35+ keys)'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🎨',
          title: t('changelog.v54.ui.title', 'UI Improvements'),
          color: '#8b5cf6',
          items: (t('changelog.v54.ui.items', { returnObjects: true, defaultValue: [
            'LanguageSwitcher with solid background & blur effect',
            'Dropdown with flag icons and native language names',
            'Keyboard accessible (Escape to close)'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🔧',
          title: t('changelog.v54.technical.title', 'Technical'),
          color: '#3b82f6',
          items: (t('changelog.v54.technical.items', { returnObjects: true, defaultValue: [
            'LANGUAGE_CONFIG with metadata (name, nativeName, flag)',
            'setLanguage() function in LanguageContext',
            'languageUserChosen flag for localStorage persistence',
            'English fallbacks for graceful degradation'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        }
      ]
    },
    {
      version: '5.3',
      title: t('changelog.v53.title', 'Security Audit & Bot Refactoring'),
      date: '9 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🔐',
          title: t('changelog.v53.security.title', 'Security Improvements'),
          color: '#ef4444',
          items: (t('changelog.v53.security.items', { returnObjects: true, defaultValue: [
            'CORS whitelist implementation on 13 serverless endpoints',
            'Admin endpoint hardened with Authorization header + rate limiting',
            'Removed sensitive data from console.log statements',
            'Fixed XSS vulnerability in Changelog (SafeCodeText component)',
            'Patched jws vulnerability in wallet-tracker-backend',
            'Centralized CORS helper at `api/advice/lib/cors.js`'
          ] }) as string[]).map(text => ({ text, type: 'security' as const }))
        },
        {
          icon: '🔧',
          title: t('changelog.v53.refactoring.title', 'Bot.tsx Refactoring'),
          color: '#8b5cf6',
          items: (t('changelog.v53.refactoring.items', { returnObjects: true, defaultValue: [
            'Reduced Bot.tsx from 2601 to 2102 lines (-19%)',
            'New `useTradingSignals` hook for signal filtering',
            'New `useBackendConnection` hook for backend status',
            'Extracted `SignalCard` component with React.memo',
            'Extracted `KPICards` component (5 memoized sub-components)',
            'Extracted `SentimentPanel` component with React.memo',
            'Extracted `SignalFilters` component with React.memo'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '⚡',
          title: t('changelog.v53.performance.title', 'Performance'),
          color: '#10b981',
          items: (t('changelog.v53.performance.items', { returnObjects: true, defaultValue: [
            'Added React.memo to SignalDetailsModal',
            'Added React.memo to BacktestResults',
            '6 new memoized components for Bot page',
            'Audit score improved: 6.6/10 → 7.5/10'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        }
      ]
    },
    {
      version: '5.2',
      title: t('changelog.v52.title', 'Community Projects & SecureFlux V5'),
      date: '6 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🚀',
          title: t('changelog.v52.projects.title', 'Projects Page'),
          color: '#ec4899',
          items: (t('changelog.v52.projects.items', { returnObjects: true, defaultValue: [
            'New community projects showcase page',
            'Voting system for favorite projects',
            'Donation feature with crypto addresses (FLUX, BTC, ETH)',
            'Project details modal with full description',
            'Filter by status (development, beta, live, planned)',
            'Full FR/EN translations'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🔐',
          title: t('changelog.v52.secureflux.title', 'SecureFlux V5 Roadmap'),
          color: '#10b981',
          items: (t('changelog.v52.secureflux.items', { returnObjects: true, defaultValue: [
            'Post-Quantum Cryptography (Kyber-1024)',
            'Automatic WhatsApp/Signal/Telegram migration',
            'Integrated crypto payments (BTC, ETH, FLUX)',
            'Mesh Networking - works without internet',
            'Gamified referral system with badges',
            'Hidden Volumes & Shamir Key Recovery'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🎨',
          title: t('changelog.v52.ui.title', 'UI Improvements'),
          color: '#8b5cf6',
          items: (t('changelog.v52.ui.items', { returnObjects: true, defaultValue: [
            'Projects menu in sidebar footer with NEW badge',
            'Sidebar/topbar border alignment fix',
            'New Community category in ModuleHub'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        }
      ]
    },
    {
      version: '5.1',
      title: t('changelog.v51.title', 'Wallet Tracker Multi-API & FluxNode dApps'),
      date: '4 Dec 2025',
      relativeDate: '',
      isCurrent: false,
      type: 'minor',
      sections: [
        {
          icon: '🔗',
          title: t('changelog.v51.walletTracker.title', 'Wallet Tracker Multi-API'),
          color: '#10b981',
          items: (t('changelog.v51.walletTracker.items', { returnObjects: true, defaultValue: [
            'DeBank Pro API integration with automatic fallback to free API',
            'Alchemy API support for Ethereum & L2 chains',
            'Moralis API for multi-chain token balances',
            'Smart aggregation: cross-reference data from multiple sources',
            'Confidence score based on API agreement'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🖥️',
          title: t('changelog.v51.fluxNode.title', 'FluxNode Monitor'),
          color: '#8b5cf6',
          items: (t('changelog.v51.fluxNode.items', { returnObjects: true, defaultValue: [
            't3 multisig SSP Wallet address support',
            'dApps Monitor tab with app details',
            'Improved node status visualization'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '⌨️',
          title: t('changelog.v51.keyboard.title', 'Keyboard Navigation'),
          color: '#3b82f6',
          items: (t('changelog.v51.keyboard.items', { returnObjects: true, defaultValue: [
            'Cmd/Ctrl+Up: Previous page',
            'Cmd/Ctrl+Down: Next page'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🌍',
          title: t('changelog.v51.i18n.title', 'Internationalization'),
          color: '#f59e0b',
          items: (t('changelog.v51.i18n.items', { returnObjects: true, defaultValue: [
            'Complete i18n coverage for Changelog page',
            'Portfolio components fully translated'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '🎨',
          title: t('changelog.v51.ux.title', 'UX Improvements'),
          color: '#ec4899',
          items: (t('changelog.v51.ux.items', { returnObjects: true, defaultValue: [
            'New app footer with version info and changelog link',
            'Updated branding: Swiss-Army-Knife Crypto Toolkit'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '📸',
          title: t('changelog.v51.snapshots.title', 'Portfolio Snapshots'),
          color: '#06b6d4',
          items: (t('changelog.v51.snapshots.items', { returnObjects: true, defaultValue: [
            'Take snapshots of your portfolio to track changes over time',
            'Compare two snapshots to see value and P&L differences',
            'View added, removed, and modified tokens between snapshots',
            'Export/import snapshots as JSON for backup'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '📱',
          title: t('changelog.v51.pwa.title', 'PWA Improvements'),
          color: '#a855f7',
          items: (t('changelog.v51.pwa.items', { returnObjects: true, defaultValue: [
            'Update notification when new version is available',
            'One-click update to refresh the app',
            'Improved service worker with smart caching',
            'What\'s New modal only for returning users (not first-time visitors)'
          ] }) as string[]).map(text => ({ text, type: 'improvement' as const }))
        },
        {
          icon: '📺',
          title: t('changelog.v51.ipadDashboard.title', 'iPad Dashboard'),
          color: '#14b8a6',
          items: (t('changelog.v51.ipadDashboard.items', { returnObjects: true, defaultValue: [
            'Clean watchlist dashboard designed for iPad wallpaper',
            'Track tokens from portfolio or external tokens',
            'Visual alerts with colors (TickrMeter-style)',
            'Auto cost basis sync from portfolio positions',
            'Configurable grid layout (2x2, 3x2, 4x2, etc.)',
            'Real-time prices with auto-refresh'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🧾',
          title: t('changelog.v51.taxCenter.title', 'Tax Center (France)'),
          color: '#8b5cf6',
          items: (t('changelog.v51.taxCenter.items', { returnObjects: true, defaultValue: [
            'French crypto tax calculation (Article 150 VH bis CGI)',
            'CSV import: Binance, Coinbase, Kraken, Crypto.com, KuCoin',
            'PDF Form 2086 - Capital gains declaration',
            'PDF Form 3916-bis - Foreign accounts declaration',
            'PDF Tax Certificate with methodology & verification hash',
            'PDF Grand Livre - Complete transaction ledger',
            'PDF Fiche de Stock - Portfolio snapshot at 31/12 with PRU',
            'PFU 30% vs Progressive tax comparison',
            '€305 annual exemption threshold detection',
            'Tax submenu grouping all fiscal tools in sidebar'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
      ]
    },
    {
      version: '5.0',
      title: t('changelog.v50.title', 'CoinGecko Pro & Risk Analysis'),
      date: '30 Nov 2025',
      relativeDate: '4 days ago',
      type: 'major',
      sections: [
        {
          icon: '🚀',
          title: t('changelog.v50.coingecko.title', 'CoinGecko Pro API'),
          color: '#10b981',
          items: (t('changelog.v50.coingecko.items', { returnObjects: true, defaultValue: [
            'Auto-detect Pro API key from environment',
            'Pro features: higher rate limits, priority access',
            'Graceful fallback to free API'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '📊',
          title: t('changelog.v50.riskAnalysis.title', 'Risk Analysis'),
          color: '#3b82f6',
          items: (t('changelog.v50.riskAnalysis.items', { returnObjects: true, defaultValue: [
            'Advanced risk metrics dashboard',
            'Portfolio correlation analysis',
            'Volatility tracking'
          ] }) as string[]).map(text => ({ text, type: 'feature' as const }))
        },
        {
          icon: '🐛',
          title: t('changelog.v50.fixes.title', 'Bug Fixes'),
          color: '#ef4444',
          items: (t('changelog.v50.fixes.items', { returnObjects: true, defaultValue: [
            'Fixed price refresh issues',
            'CSP policy updates for pro-api.coingecko.com'
          ] }) as string[]).map(text => ({ text, type: 'fix' as const }))
        },
      ]
    },
    {
      version: '4.9',
      title: 'Module Hub & Hedera Monitor',
      date: '25 Nov 2025',
      relativeDate: '9 days ago',
      type: 'minor',
      sections: [
        {
          icon: '🧩',
          title: 'Module Hub',
          color: '#8b5cf6',
          items: [
            { text: 'Centralized module management dashboard', type: 'feature' },
            { text: 'Enable/disable modules dynamically', type: 'feature' },
            { text: 'Module health status indicators', type: 'feature' },
          ]
        },
        {
          icon: '💎',
          title: 'Hedera HBAR Monitor',
          color: '#10b981',
          items: [
            { text: 'Real-time HBAR account tracking', type: 'feature' },
            { text: 'Transaction history with filters', type: 'feature' },
            { text: 'Staking rewards visualization', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '4.8',
      title: 'Trading Bot Autopilot',
      date: '15 Nov 2025',
      type: 'minor',
      sections: [
        {
          icon: '🤖',
          title: 'Autopilot Bot',
          color: '#f59e0b',
          items: [
            { text: 'Infinity Grid strategy implementation', type: 'feature' },
            { text: 'Smart DCA (Dollar Cost Averaging)', type: 'feature' },
            { text: 'BTC Rebalancer strategy', type: 'feature' },
            { text: 'Binance API integration', type: 'feature' },
            { text: 'Testnet mode for safe testing', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '4.7',
      title: 'FluxNode Monitor',
      date: '1 Nov 2025',
      type: 'minor',
      sections: [
        {
          icon: '🖥️',
          title: 'Node Monitoring',
          color: '#3b82f6',
          items: [
            { text: 'FluxNode status monitoring (CUMULUS/NIMBUS/STRATUS)', type: 'feature' },
            { text: 'Rewards tracking and projections', type: 'feature' },
            { text: 'Uptime statistics', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '4.5',
      title: 'Advanced Visualizations',
      date: '15 Oct 2025',
      type: 'minor',
      sections: [
        {
          icon: '📊',
          title: '28+ Visualizations',
          color: '#ec4899',
          items: [
            { text: 'Portfolio 3D Universe (Three.js)', type: 'feature' },
            { text: 'TreeMap, Sankey diagrams', type: 'feature' },
            { text: 'Performance heatmaps', type: 'feature' },
            { text: 'Correlation matrices', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '4.0',
      title: 'Academy & Learning',
      date: '1 Oct 2025',
      type: 'major',
      sections: [
        {
          icon: '🎓',
          title: 'Crypto Academy',
          color: '#10b981',
          items: [
            { text: 'Interactive courses and lessons', type: 'feature' },
            { text: 'Achievement and badge system', type: 'feature' },
            { text: 'Progress tracking', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '3.0',
      title: 'Multi-Portfolio & Alerts',
      date: '1 Sep 2025',
      type: 'major',
      sections: [
        {
          icon: '📁',
          title: 'Multi-Portfolio',
          color: '#8b5cf6',
          items: [
            { text: 'Create and manage multiple portfolios', type: 'feature' },
            { text: 'Portfolio comparison tools', type: 'feature' },
            { text: 'Import/Export portfolios', type: 'feature' },
          ]
        },
        {
          icon: '🔔',
          title: 'Price Alerts',
          color: '#f59e0b',
          items: [
            { text: 'Custom price alerts', type: 'feature' },
            { text: 'Notification system', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '2.0',
      title: 'Tax Calculator & Analytics',
      date: '1 Aug 2025',
      type: 'major',
      sections: [
        {
          icon: '📈',
          title: 'Tax & Analytics',
          color: '#10b981',
          items: [
            { text: 'FIFO/LIFO/Average cost calculation', type: 'feature' },
            { text: 'Capital gains tracking', type: 'feature' },
            { text: 'Performance analytics', type: 'feature' },
            { text: 'Export for tax reporting', type: 'feature' },
          ]
        },
      ]
    },
    {
      version: '1.0',
      title: 'Initial Launch',
      date: 'Sep 2024',
      type: 'major',
      sections: [
        {
          icon: '🚀',
          title: 'Foundation',
          color: '#3b82f6',
          items: [
            { text: 'React 18 + TypeScript + Vite architecture', type: 'feature' },
            { text: 'Portfolio management (add/edit/delete)', type: 'feature' },
            { text: 'Dark mode by default', type: 'feature' },
            { text: 'LocalStorage persistence', type: 'feature' },
            { text: 'PWA ready with offline support', type: 'feature' },
          ]
        },
      ]
    },
  ], [t]);

  // Filter versions by search query
  const filteredVersions = useMemo(() => {
    if (!searchQuery.trim()) return versions;
    const query = searchQuery.toLowerCase();
    return versions.filter(v =>
      v.title.toLowerCase().includes(query) ||
      v.version.includes(query) ||
      v.sections.some(s =>
        s.title.toLowerCase().includes(query) ||
        s.items.some(i => i.text.toLowerCase().includes(query))
      )
    );
  }, [versions, searchQuery]);

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>

      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ marginBottom: '8px', fontSize: '28px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Icons.FileText size={32} />
              {t('changelog.title', 'Changelog')}
            </h1>
            <p style={{ lineHeight: '1.6', fontSize: '14px', color: 'var(--muted)', maxWidth: '600px' }}>
              {t('changelog.description', 'Complete version history and release notes for Edouard.ai. All features, bug fixes, and improvements are documented here.')}
            </p>
          </div>

          {/* Search & Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ position: 'relative' }}>
              <Icons.Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)',
              }} />
              <input
                type="text"
                placeholder="Search changelog..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '10px 12px 10px 38px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  fontSize: '14px',
                  width: '220px',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={expandAll}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <ChangelogStats versions={versions} />

      {/* Quick Navigation */}
      <QuickNav versions={versions} onJump={jumpToVersion} />

      {/* Timeline */}
      <div style={{
        position: 'relative',
        paddingLeft: '20px',
      }}>
        {/* Timeline line */}
        <div style={{
          position: 'absolute',
          left: '26px',
          top: '40px',
          bottom: '40px',
          width: '2px',
          background: 'linear-gradient(to bottom, var(--accent), var(--border))',
          borderRadius: '2px',
        }} />

        {/* Version cards */}
        {filteredVersions.map((version, index) => (
          <div key={version.version} id={`version-${version.version}`}>
            <VersionCard
              version={version}
              isExpanded={expandedVersions.has(version.version)}
              onToggle={() => toggleVersion(version.version)}
              index={index}
            />
          </div>
        ))}

        {filteredVersions.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '40px', marginLeft: '20px' }}>
            <Icons.Search size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--muted)' }}>No results found for "{searchQuery}"</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="card" style={{ textAlign: 'center', padding: '24px', marginTop: '8px' }}>
        <p style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6' }}>
          📝 {t('changelog.footer.note', 'This changelog is updated with each major version.')}<br />
          {t('changelog.footer.gitDetails', 'For complete technical details, see the Git repository.')}
        </p>
      </div>
    </>
  );
}
